import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

interface AnimatedKpiState {
  value: number;
  pctChange: number; // -8 to +8
  isAlert: boolean;
  direction: 'up' | 'down' | 'stable';
}

/**
 * Hook that animates a base value with slow random walk within ±8%.
 * Occasionally flashes red when hitting ±8% extremes.
 * Alerts are throttled to max once per minute.
 */
export function useAnimatedKpi(baseValue: number, label: string, unit: string): AnimatedKpiState {
  const [state, setState] = useState<AnimatedKpiState>({
    value: baseValue,
    pctChange: 0,
    isAlert: false,
    direction: 'stable',
  });

  const lastAlertRef = useRef(0);
  const phaseRef = useRef(Math.random() * Math.PI * 2);
  const speedRef = useRef(0.3 + Math.random() * 0.4); // random speed per param

  useEffect(() => {
    if (baseValue <= 0) return;

    let rafId: number;
    const startTime = Date.now();

    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const phase = phaseRef.current;
      const speed = speedRef.current;

      // Combine slow sine waves for smooth organic movement
      const wave1 = Math.sin(elapsed * speed + phase);
      const wave2 = Math.sin(elapsed * speed * 0.7 + phase * 1.3);
      const combined = (wave1 * 0.6 + wave2 * 0.4); // range roughly -1 to +1

      // Scale to ±8%
      const pctChange = combined * 8; // -8% to +8%
      const newValue = baseValue * (1 + pctChange / 100);

      // Determine if at extreme
      const atExtreme = Math.abs(pctChange) >= 7.5;
      const now = Date.now();
      const canAlert = now - lastAlertRef.current > 60000; // 1 minute throttle

      let isAlert = false;
      if (atExtreme && canAlert) {
        isAlert = true;
        lastAlertRef.current = now;
        const direction = pctChange > 0 ? 'above' : 'below';
        toast(
          <div className="flex items-start gap-2">
            <span className="text-amber-400 mt-0.5">⚠</span>
            <div>
              <p className="text-sm font-semibold text-white">{label} Alert</p>
              <p className="text-xs text-slate-400">{label} is {direction} normal range. Value: {newValue.toFixed(1)} {unit}. Check influent source.</p>
            </div>
          </div>,
          { duration: 8000 }
        );
      }

      const direction = pctChange > 0.5 ? 'up' : pctChange < -0.5 ? 'down' : 'stable';

      setState({
        value: newValue,
        pctChange,
        isAlert: atExtreme,
        direction,
      });

      rafId = window.setTimeout(tick, 2500 + Math.random() * 1000); // 2.5-3.5s intervals
    };

    tick();
    return () => clearTimeout(rafId);
  }, [baseValue, label, unit]);

  return state;
}

/**
 * Generates a flowing sparkline data array from a base value.
 * Data varies by ±8% and is appended over time.
 */
export function useFlowingSparkline(baseValue: number, length = 12): number[] {
  const [data, setData] = useState<number[]>(() =>
    Array.from({ length }, () => baseValue * (1 + (Math.random() - 0.5) * 0.16))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setData(prev => {
        const next = baseValue * (1 + (Math.random() - 0.5) * 0.16);
        return [...prev.slice(1), next];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [baseValue, length]);

  return data;
}
